import React from 'react';
import { Info, DollarSign, Calculator, PiggyBank, Zap, AlertTriangle, FileText, Globe, User } from 'lucide-react';

/**
 * Компонент с информацией о НДФЛ в России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о НДФЛ: налоговая система России
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Что такое НДФЛ?
        </h3>
        <p>
          <strong>Налог на доходы физических лиц (НДФЛ)</strong> — это прямой налог, который взимается с доходов физических лиц в России. Он является федеральным и зачисляется в бюджеты разных уровней. НДФЛ регулируется главой 23 Налогового кодекса РФ.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Основные элементы НДФЛ
        </h3>
        <ul>
          <li><strong>Налогоплательщики:</strong> Физические лица, являющиеся налоговыми резидентами РФ (находятся в России не менее 183 дней в течение 12 месяцев подряд), а также нерезиденты, получающие доходы от источников в РФ.</li>
          <li><strong>Объект налогообложения:</strong> Доходы физических лиц, полученные в денежной или натуральной форме.</li>
          <li><strong>Налоговая база:</strong> Общая сумма доходов, уменьшенная на сумму налоговых вычетов.</li>
          <li><strong>Налоговый период:</strong> Календарный год.</li>
          <li>
            <strong>Налоговые ставки:</strong>
            <ul>
              <li><strong>13%</strong> — основная ставка для резидентов (до 5 млн ₽ в год);</li>
              <li><strong>15%</strong> — для резидентов на доходы свыше 5 млн ₽ в год;</li>
              <li><strong>15%</strong> — для нерезидентов по отдельным категориям доходов;</li>
              <li><strong>35%</strong> — для отдельных видов доходов (выигрыши, призы, процентные доходы по вкладам сверх установленного лимита и др.).</li>
            </ul>
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          Особенности налогообложения для резидентов и нерезидентов
        </h3>
        <p>
          Налоговые резиденты и нерезиденты РФ имеют разный налоговый статус, что влияет на порядок налогообложения их доходов:
        </p>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">Параметр</th>
              <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">Резиденты</th>
              <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">Нерезиденты</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Налогооблагаемые доходы</td>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Доходы от источников в РФ и за рубежом</td>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Только доходы от источников в РФ</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Основная ставка НДФЛ</td>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">13% (15% с дохода свыше 5 млн ₽)</td>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">15%</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Налоговые вычеты</td>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Доступны все виды вычетов</td>
              <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">Не предоставляются</td>
            </tr>
          </tbody>
        </table>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Налоговые вычеты
        </h3>
        <p>
          Налоговые вычеты — это суммы, которые уменьшают налогооблагаемую базу или возвращают уже уплаченный НДФЛ. Они доступны только для налоговых резидентов РФ. Основные типы вычетов:
        </p>
        <ul>
          <li>
            <strong>Стандартные вычеты:</strong> Предоставляются отдельным категориям налогоплательщиков и на детей.
            <ul>
              <li>Вычет на первого и второго ребенка — 1 400 ₽ в месяц;</li>
              <li>Вычет на третьего и последующих детей — 3 000 ₽ в месяц;</li>
              <li>Вычет на ребенка-инвалида — 12 000 ₽ в месяц.</li>
            </ul>
          </li>
          <li>
            <strong>Социальные вычеты:</strong> Предоставляются при расходах на обучение, лечение, благотворительность, пенсионное обеспечение и страхование.
            <ul>
              <li>Обучение — до 50 000 ₽ в год за себя и до 50 000 ₽ за каждого ребенка;</li>
              <li>Лечение — в рамках общего лимита 120 000 ₽ в год (дорогостоящее лечение — без ограничений);</li>
              <li>Пенсионные взносы — в рамках общего лимита 120 000 ₽ в год;</li>
              <li>Благотворительность — до 25% годового дохода.</li>
            </ul>
          </li>
          <li>
            <strong>Имущественные вычеты:</strong> Предоставляются при покупке жилья и продаже имущества.
            <ul>
              <li>Покупка жилья — до 2 000 000 ₽ (возврат до 260 000 ₽ НДФЛ);</li>
              <li>Проценты по ипотеке — до 3 000 000 ₽ (возврат до 390 000 ₽ НДФЛ);</li>
              <li>Продажа недвижимости в собственности менее 5 лет (менее 3 лет для отдельных случаев) — до 1 000 000 ₽.</li>
            </ul>
          </li>
          <li>
            <strong>Инвестиционные вычеты:</strong> Предоставляются инвесторам при операциях с ценными бумагами и при взносах на ИИС.
            <ul>
              <li>Тип А (взносы на ИИС) — до 400 000 ₽ в год (возврат до 52 000 ₽ НДФЛ);</li>
              <li>Тип Б (доход от операций на ИИС) — освобождение от НДФЛ всего дохода при закрытии счета через 3 года.</li>
            </ul>
          </li>
          <li>
            <strong>Профессиональные вычеты:</strong> Предоставляются при расходах, связанных с предпринимательской или профессиональной деятельностью.
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Важное изменение в НДФЛ с 2021 года</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            С 1 января 2021 года в России введена прогрессивная шкала НДФЛ: доходы до 5 млн ₽ в год облагаются по ставке 13%, а доходы свыше этой суммы — по ставке 15%. Повышенная ставка применяется только к сумме превышения.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <User className="w-5 h-5 text-purple-500" />
          Специальные налоговые режимы
        </h3>
        <h4>Индивидуальные предприниматели</h4>
        <p>
          ИП могут применять различные системы налогообложения, которые влияют на порядок уплаты НДФЛ:
        </p>
        <ul>
          <li><strong>Общая система налогообложения (ОСНО)</strong> — ИП платит НДФЛ по ставке 13%/15% с доходов за вычетом расходов;</li>
          <li><strong>Упрощенная система налогообложения (УСН)</strong> — заменяет НДФЛ:
            <ul>
              <li>УСН "Доходы" — 6% от доходов;</li>
              <li>УСН "Доходы минус расходы" — 15% от разницы между доходами и расходами.</li>
            </ul>
          </li>
          <li><strong>Патентная система налогообложения (ПСН)</strong> — стоимость патента заменяет НДФЛ для конкретного вида деятельности;</li>
          <li><strong>Единый сельскохозяйственный налог (ЕСХН)</strong> — 6% от разницы между доходами и расходами для сельхозпроизводителей.</li>
        </ul>

        <h4>Самозанятые</h4>
        <p>
          Специальный налоговый режим "Налог на профессиональный доход" (НПД) для самозанятых предусматривает следующие ставки:
        </p>
        <ul>
          <li>4% от доходов, полученных от физических лиц;</li>
          <li>6% от доходов, полученных от ИП и юридических лиц.</li>
        </ul>
        <p>
          Этот режим заменяет НДФЛ для доходов от профессиональной деятельности в пределах лимита 2,4 млн ₽ в год. Самозанятым не нужно подавать налоговую декларацию и платить страховые взносы.
        </p>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Декларирование доходов
        </h3>
        <p>
          Декларация по НДФЛ (форма 3-НДФЛ) подается в следующих случаях:
        </p>
        <ul>
          <li>При получении доходов, с которых не был удержан НДФЛ;</li>
          <li>При получении доходов от продажи имущества;</li>
          <li>При получении доходов от источников за пределами РФ (для резидентов);</li>
          <li>При получении выигрышей, призов;</li>
          <li>Для получения налоговых вычетов;</li>
          <li>ИП на ОСНО обязаны подавать декларацию ежегодно.</li>
        </ul>
        <p>
          Срок подачи декларации — не позднее 30 апреля года, следующего за отчетным. Срок уплаты налога — не позднее 15 июля года, следующего за отчетным.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при расчете НДФЛ
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Проверьте свой налоговый статус (резидент или нерезидент)</li>
            <li>Учтите все источники доходов, в том числе зарубежные</li>
            <li>Используйте доступные налоговые вычеты для минимизации налоговой нагрузки</li>
            <li>Соблюдайте сроки подачи декларации и уплаты налога</li>
            <li>При наличии доходов из стран, с которыми у России есть соглашения об избежании двойного налогообложения, учитывайте возможность зачета налога</li>
          </ul>
        </div>

        <h3>Как минимизировать НДФЛ законным способом?</h3>
        <ul>
          <li>Используйте все доступные налоговые вычеты</li>
          <li>Планируйте сроки продажи имущества с учетом минимальных сроков владения</li>
          <li>Рассмотрите возможность открытия ИИС с налоговым вычетом</li>
          <li>ИП могут выбрать оптимальную систему налогообложения</li>
          <li>При наличии возможности распределите доходы во времени для избежания превышения порога в 5 млн ₽</li>
          <li>Документально подтверждайте все расходы, которые могут быть учтены для уменьшения налоговой базы</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор НДФЛ поможет вам рассчитать сумму налога с учетом всех особенностей и найти возможности для налоговой оптимизации!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;